// SuperAdminDashboard.js
// Place this in your src/components or src/pages folder
// Route it at  /Superadmindashboard  (accessible only if role === 'super_admin')

import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Clock, Activity, Settings, RefreshCw,
  ChevronDown, ChevronUp, CheckCircle, XCircle,
  LogIn, LogOut, Shield, Eye, EyeOff
} from 'lucide-react';

const API = 'https://dolphin-app-33jp4.ondigitalocean.app/isfathena';

// ─── helpers ────────────────────────────────────────────────────────

// ✅ BUG FIX #1 — Timezone fix
// The DB stores all times as UTC (after the main.py fix).
// We append 'Z' so JavaScript knows the string is UTC, then
// convert to IST using timeZone: 'Asia/Kolkata'.
const fmt = (iso) => {
  if (!iso || iso === 'Active now') return iso;
  // Append 'Z' only if no timezone info is present in the string
  const utcIso = (iso.endsWith('Z') || iso.includes('+') || iso.includes('-', 10))
    ? iso
    : iso + 'Z';
  return new Date(utcIso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    timeZone: 'Asia/Kolkata',   // ← Force IST display
  });
};

const secToHuman = (totalSec) => {
  if (!totalSec || totalSec < 1) return '0s';
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = Math.round(totalSec % 60);
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const minToHuman = (m) => secToHuman(Math.round((m || 0) * 60));

// ─── sub-components ─────────────────────────────────────────────────

const Badge = ({ active }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold
    ${active
      ? 'bg-green-500/20 text-green-400 border border-green-500/40'
      : 'bg-gray-700 text-gray-400 border border-gray-600'}`}>
    <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-green-400 animate-pulse' : 'bg-gray-500'}`} />
    {active ? 'Active now' : 'Offline'}
  </span>
);

// ─── Session history row (expandable) ───────────────────────────────
const UserRow = ({ user, refAccess, onToggleAccess }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Main row */}
      <tr className="border-b border-gray-700 hover:bg-gray-800/50 transition-colors">
        <td className="px-4 py-3">
          <div className="font-semibold text-white text-sm">{user.name}</div>
          <div className="text-gray-400 text-xs">{user.email}</div>
        </td>
        <td className="px-4 py-3 text-center">
          <Badge active={user.is_active_now} />
        </td>
        <td className="px-4 py-3 text-center text-sm text-gray-300">{user.total_logins}</td>
        <td className="px-4 py-3 text-center text-sm text-gray-300">
          {minToHuman(user.total_time_min)}
        </td>
        <td className="px-4 py-3 text-center">
          <button
            onClick={() => onToggleAccess(user.email, !refAccess)}
            title={refAccess ? 'Revoke reference access' : 'Grant reference access'}
            className={`p-1.5 rounded-lg border transition-all ${
              refAccess
                ? 'bg-orange-500/20 border-orange-500/50 text-orange-400 hover:bg-orange-500/30'
                : 'bg-gray-700 border-gray-600 text-gray-400 hover:bg-gray-600'
            }`}>
            {refAccess ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </button>
        </td>
        <td className="px-4 py-3 text-center">
          <button
            onClick={() => setOpen(!open)}
            className="text-gray-400 hover:text-orange-400 transition-colors">
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </td>
      </tr>

      {/* Session history (expanded) */}
      {open && (
        <tr className="bg-gray-900/60">
          <td colSpan={6} className="px-6 py-4">
            {user.sessions.length === 0 ? (
              <p className="text-gray-500 text-sm italic">No login sessions recorded yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500 border-b border-gray-700">
                      <th className="text-left py-1 pr-4">#</th>
                      <th className="text-left py-1 pr-4">Login time</th>
                      <th className="text-left py-1 pr-4">Logout time</th>
                      <th className="text-left py-1">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {user.sessions.map((s, i) => (
                      <tr key={s.session_id}
                        className={`border-b border-gray-800 ${s.is_active ? 'text-green-400' : 'text-gray-300'}`}>
                        <td className="py-1 pr-4">{i + 1}</td>
                        <td className="py-1 pr-4 flex items-center gap-1">
                          <LogIn className="w-3 h-3" /> {fmt(s.login_time)}
                        </td>
                        <td className="py-1 pr-4">
                          {s.logout_time === 'Active now'
                            ? <span className="flex items-center gap-1"><Activity className="w-3 h-3 animate-pulse" /> Active now</span>
                            : <span className="flex items-center gap-1"><LogOut className="w-3 h-3" />{fmt(s.logout_time)}</span>}
                        </td>
                        <td className="py-1">{secToHuman(s.duration_sec ?? Math.round(s.duration_min * 60))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

// ─── Configurations Modal ────────────────────────────────────────────
const ConfigurationsModal = ({ users, refAccessMap, onToggle, onClose }) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
    <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
      <div className="flex items-center justify-between p-6 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-orange-400" />
          <h2 className="text-lg font-bold text-white">Reference Page Access</h2>
        </div>
        <button onClick={onClose}
          className="text-gray-400 hover:text-white text-xl leading-none transition-colors">✕</button>
      </div>

      <div className="overflow-y-auto flex-1 p-6 space-y-3">
        {users.map((u) => {
          const hasAccess = refAccessMap[u.email] ?? false;
          return (
            <div key={u.email}
              className="flex items-center justify-between bg-gray-800 rounded-xl p-4 border border-gray-700">
              <div>
                <div className="font-semibold text-white text-sm">{u.name}</div>
                <div className="text-gray-400 text-xs">{u.email}</div>
                <div className="text-gray-500 text-xs">{u.membership}</div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <span className={`text-xs font-semibold ${hasAccess ? 'text-green-400' : 'text-gray-500'}`}>
                  {hasAccess ? 'Allowed' : 'Blocked'}
                </span>
                <div
                  onClick={() => onToggle(u.email, !hasAccess)}
                  className={`relative w-11 h-6 rounded-full transition-colors duration-300 cursor-pointer
                    ${hasAccess ? 'bg-orange-500' : 'bg-gray-600'}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-300
                    ${hasAccess ? 'translate-x-5' : 'translate-x-0'}`} />
                </div>
              </label>
            </div>
          );
        })}
      </div>

      <div className="p-4 border-t border-gray-700 text-center">
        <p className="text-gray-500 text-xs">Changes take effect immediately — users will see/lose the Reference page within 10 seconds.</p>
      </div>
    </div>
  </div>
);

// ─── MAIN DASHBOARD ─────────────────────────────────────────────────
export default function SuperAdminDashboard() {
  const [users,          setUsers]          = useState([]);
  const [refAccessMap,   setRefAccessMap]   = useState({});  // { email: bool }
  const [loading,        setLoading]        = useState(true);
  const [showConfig,     setShowConfig]     = useState(false);
  const [lastRefresh,    setLastRefresh]    = useState(null);

  // ── Fetch user list + session data ──────────────────────────────
  const fetchUsers = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const res  = await fetch(`${API}/admin/users`);
      const data = await res.json();
      if (data.success) setUsers(data.users || []);
    } catch (e) {
      console.error('Failed to fetch users', e);
    } finally {
      if (showSpinner) setLoading(false);
      setLastRefresh(new Date());
    }
  }, []);

  // ── Fetch reference access list ─────────────────────────────────
  const fetchRefAccess = useCallback(async () => {
    try {
      const res  = await fetch(`${API}/admin/reference-access`);
      const data = await res.json();
      if (data.success) {
        const map = {};
        (data.users || []).forEach((u) => { map[u.email] = Boolean(u.can_access); });
        setRefAccessMap(map);
      }
    } catch (e) {
      console.error('Failed to fetch reference access', e);
    }
  }, []);

  useEffect(() => {
    fetchUsers(true);  // show spinner only on first load
    fetchRefAccess();

    // ✅ FIX: Page refresh ke baad 1.5s wait karke fetch karo
    // Taaki backend ko time mile user ko dobara "active" mark karne ka
    const refreshDelay = sessionStorage.getItem('dashboardWasOpen') ? 1500 : 0;
    sessionStorage.setItem('dashboardWasOpen', 'true');

    const timer = setTimeout(() => {
      fetchUsers(false);
    }, refreshDelay);

    const interval = setInterval(() => fetchUsers(false), 10000); // auto-refresh every 10s silently
    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, [fetchUsers, fetchRefAccess]);

  // ── Toggle reference access ──────────────────────────────────────
  const handleToggleAccess = async (email, newState) => {
    // ✅ Optimistic update — UI changes immediately
    setRefAccessMap((prev) => ({ ...prev, [email]: newState }));

    // ✅ BUG FIX #2 & #3 — Broadcast change to ALL open tabs/windows
    // This fires storage event in other tabs so their Navbar updates instantly.
    // We also store the toggled email so Navbar.js can pick it up.
    try {
      localStorage.setItem('refAccessToggle', JSON.stringify({ email, value: newState ? '1' : '0', ts: Date.now() }));
    } catch {}

    // If the current logged-in super admin's own access is being toggled
    try {
      const raw = localStorage.getItem('userData');
      if (raw) {
        const me = JSON.parse(raw);
        if (me.email === email) {
          localStorage.setItem('refAccess', newState ? '1' : '0');
          window.dispatchEvent(new Event('refAccessChange'));
        }
      }
    } catch {}

    try {
      const body = new URLSearchParams({ email, can_access: newState ? 1 : 0 });
      await fetch(`${API}/admin/reference-access/toggle`, { method: 'POST', body });
    } catch (e) {
      // Rollback on failure
      setRefAccessMap((prev) => ({ ...prev, [email]: !newState }));
      console.error('Toggle failed', e);
    }
  };

  // ── Derived stats ────────────────────────────────────────────────
  const activeCount  = users.filter((u) => u.is_active_now).length;
  const totalLogins  = users.reduce((s, u) => s + u.total_logins, 0);
  const totalMinutes = users.reduce((s, u) => s + u.total_time_min, 0);

  // ── Guard: only super_admin can see this page ────────────────────
  useEffect(() => {
    const raw = localStorage.getItem('userData');
    if (!raw) { window.location.href = '/login'; return; }
    const u = JSON.parse(raw);
    if (u.role !== 'super_admin') window.location.href = '/';
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black pt-20 px-4 pb-12">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Super Admin Dashboard</h1>
            {lastRefresh && (
              <p className="text-gray-500 text-xs mt-1">
                Last refreshed: {lastRefresh.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' })}
              </p>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => { fetchUsers(true); fetchRefAccess(); }}
              className="flex items-center gap-2 px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-xl text-sm font-semibold hover:bg-gray-600 transition-all">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button
              onClick={() => setShowConfig(true)}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-xl text-sm font-bold hover:bg-orange-600 transition-all shadow-lg shadow-orange-500/30">
              <Settings className="w-4 h-4" /> Configurations
            </button>
          </div>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { icon: Users,    label: 'Total Users',   value: users.length,          color: 'blue'   },
            { icon: Activity, label: 'Active Now',    value: activeCount,            color: 'green'  },
            { icon: LogIn,    label: 'Total Logins',  value: totalLogins,            color: 'purple' },
            { icon: Clock,    label: 'Total Time',    value: minToHuman(totalMinutes), color: 'orange' },
          ].map(({ icon: Icon, label, value, color }) => (
            <div key={label}
              className="bg-gray-800/60 border border-gray-700 rounded-2xl p-5 flex items-center gap-4">
              <div className={`p-3 rounded-xl bg-${color}-500/20 border border-${color}-500/30`}>
                <Icon className={`w-5 h-5 text-${color}-400`} />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{value}</div>
                <div className="text-gray-400 text-xs">{label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Users table */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-2xl overflow-hidden">
          <div className="p-5 border-b border-gray-700">
            <h2 className="font-bold text-white text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-400" /> User Activity
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-16">
              <div className="w-10 h-10 border-4 border-gray-600 border-t-orange-500 rounded-full animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-700 bg-gray-900/40">
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3 text-center">Status</th>
                    <th className="px-4 py-3 text-center">Logins</th>
                    <th className="px-4 py-3 text-center">Time Spent</th>
                    <th className="px-4 py-3 text-center">Reference</th>
                    <th className="px-4 py-3 text-center">History</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-gray-500 py-12 text-sm">
                        No users found.
                      </td>
                    </tr>
                  ) : (
                    users.map((u) => (
                      <UserRow
                        key={u.email}
                        user={u}
                        refAccess={refAccessMap[u.email] ?? false}
                        onToggleAccess={handleToggleAccess}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Active users call-out */}
        {activeCount > 0 && (
          <div className="mt-6 bg-green-500/10 border border-green-500/30 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Activity className="w-5 h-5 text-green-400 animate-pulse" />
              <h3 className="font-bold text-green-400">Currently Online ({activeCount})</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {users.filter((u) => u.is_active_now).map((u) => (
                <span key={u.email}
                  className="bg-green-500/20 border border-green-500/30 text-green-300 rounded-full px-3 py-1 text-xs font-semibold">
                  {u.email}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Configurations Modal */}
      {showConfig && (
        <ConfigurationsModal
          users={users}
          refAccessMap={refAccessMap}
          onToggle={handleToggleAccess}
          onClose={() => setShowConfig(false)}
        />
      )}
    </div>
  );
}